import { z } from 'zod';
import { latSchema, lonSchema, timestampSchema } from '../domain/types.js';
/**
 * Die Navigation zur Kolonne — Stufe 2.
 *
 * Nicht der schnellste Weg zur Live-Position der Führung, sondern der Weg
 * **entlang der tatsächlich gefahrenen Spur**. Sonst schickt die App den Fahrer
 * über die Bundesstraße und er verpasst genau die Strecke, um die es geht.
 *
 * Eine Route besteht aus zwei Abschnitten, die zu einer durchgehenden
 * Hinweisliste zusammengesetzt sind: dem Rückweg auf die Spur — eine gewöhnliche
 * Routenberechnung — und der eingerasteten Führungsspur ab dem Wiedereinstieg
 * bis zum Stand der Führung.
 *
 * Alles hier ist frei von Datenbank und Routingdienst: Die Typen sind zugleich
 * Protokoll und werden zur Laufzeit geprüft.
 */
/**
 * Art eines Manövers — die Grundlage des Sinnbilds auf dem Bildschirm.
 *
 * Bewusst grob: Ein Fahrer im Auto unterscheidet im Vorbeischauen nicht
 * zwischen sieben Abstufungen von rechts. Das Zeichen sagt die Seite, der
 * Hinweistext daneben sagt, wie scharf — und der steht ohnehin groß da.
 *
 * Auffahrten und Ausfahrten haben deshalb keine eigene Art: Sie sind ein
 * Abbiegen nach links oder rechts, und genau das braucht der Fahrer zu wissen.
 */
export const maneuverKindSchema = z.enum([
    'start',
    'straight',
    'slight_left',
    'left',
    'sharp_left',
    'uturn',
    'slight_right',
    'right',
    'sharp_right',
    'roundabout',
    'merge',
    'ferry',
    'destination',
]);
export const maneuverSchema = z.object({
    kind: maneuverKindSchema,
    /** Vollständiger Hinweis, wie er auf dem Bildschirm steht. */
    instruction: z.string(),
    /**
     * Kurzfassung für die Ansage. Der Routingdienst liefert sie in derselben
     * Anfrage mit; ohne Ton bleibt alles über `instruction` lesbar.
     */
    verbal: z.string().nullable(),
    /** Straßennamen nach dem Manöver — Bestätigung für den Fahrer. */
    streetNames: z.array(z.string()),
    /** Strecke von diesem Manöver bis zum nächsten, in Metern. */
    lengthM: z.number().nonnegative(),
    /** Fahrzeit von diesem Manöver bis zum nächsten, in Millisekunden. */
    travelMs: z.number().nonnegative(),
    /** Stelle in der Geometrie, an der das Manöver beginnt. */
    shapeIndex: z.number().int().nonnegative(),
    /** Nummer der Ausfahrt im Kreisverkehr, sonst null. */
    roundaboutExit: z.number().int().positive().nullable(),
});
export const navigationRouteSchema = z.object({
    /** Durchgehende Hinweisliste über beide Abschnitte. */
    maneuvers: z.array(maneuverSchema),
    /** Geometrie der gesamten Route. */
    shape: z.array(z.object({ lat: latSchema, lon: lonSchema })),
    lengthM: z.number().nonnegative(),
    travelMs: z.number().nonnegative(),
    /**
     * Ab welcher Stelle der Geometrie die Route auf der Führungsspur liegt.
     * Davor liegt der Rückweg; ist er nicht nötig, steht hier 0.
     */
    rejoinShapeIndex: z.number().int().nonnegative(),
    /**
     * Folgt der zweite Abschnitt der tatsächlich gefahrenen Spur?
     *
     * `false` heißt: Die Spur ließ sich nicht heranziehen — etwa weil das
     * Fahrzeug in einem früheren Abschnitt der Ausfahrt gelotet wurde. Dann ist
     * dies der gewöhnliche Weg zur Führung, und der Fahrer erfährt das auch. Eine
     * Route, die aussieht wie die gefahrene Strecke und es nicht ist, wäre
     * schlimmer als gar keine.
     */
    alongTrack: z.boolean(),
    computedAt: timestampSchema,
});
/**
 * Der letzte Eintrag der Hinweisliste — und er ist kein Ankunftssatz.
 *
 * Er ist das **nächste** Manöver, sobald keine Abbiegung mehr dazwischenliegt.
 * Auf gerader Strecke ist das schon einen Kilometer vorher der Fall. Bis zum
 * 20.08.2026 stand dort „Du bist wieder bei der Kolonne", während die Fußzeile
 * daneben „noch 1,1 km" meldete — zwei Angaben, die sich widersprachen.
 *
 * Deshalb vorwärts gerichtet und mit Entfernung wie jeder andere Hinweis. Die
 * Ankunft meldet, wer sie feststellt: das Ende der Navigation, sobald die
 * Reststrecke `ROUTING.arrivalDistanceM` unterschreitet.
 */
export const DESTINATION_INSTRUCTION = 'Weiter bis zur Kolonne.';
//# sourceMappingURL=types.js.map